import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { confirmAlert } from "react-confirm-alert";
import { findByCardapioCodigo, remove, save } from "./api-cardapio-semana";
import { dayOfWeekName } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";
import CardapioSemanaForm from "./CardapioSemanaForm";
import { REFEICOES, SEMANAS } from "./cardapioSemanaUtil";

const DIAS_UTEIS = [1, 2, 3, 4, 5];

/** Refeições do cardápio organizadas por semana (1ª a 4ª) e dia. */
const CardapioSemanaContainer = ({ cardapio }) => {
  const [listaCS, setListaCS] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [semana, setSemana] = useState(1);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    findByCardapioCodigo(cardapio.codigo)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar as refeições.");
        } else if (Array.isArray(data)) {
          setListaCS(data);
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cardapio.codigo]);

  const handleSave = (persist) => {
    const eNovo = !persist.codigo;
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar as refeições.");
          return;
        }
        setListaCS((prevState) =>
          eNovo ? [...prevState, data] : prevState.map((cs) => (cs.codigo === data.codigo ? data : cs)),
        );
        setSemana(Number(data.numeroSemana) || semana);
        setEditando(null);
        success("Salvo com sucesso.");
      })
      .finally(() => setIsSaving(false));
  };

  const handleRemove = (cs) =>
    confirmAlert({
      title: "Alerta",
      message: `Deseja remover as refeições de ${dayOfWeekName(cs.diaSemana)} da ${cs.numeroSemana}ª semana?`,
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            remove(cs.codigo).then((data) => {
              if (!data || data.error) {
                errorMessage(data?.message || "Não foi possível remover as refeições.");
                return;
              }
              setListaCS((prevState) => prevState.filter((ps) => ps.codigo !== cs.codigo));
              setEditando(null);
              success("Refeições removidas.");
            });
          },
        },
        { label: "Não", onClick: () => {} },
      ],
    });

  if (isLoading) {
    return (
      <Box py={6}>
        <Loading description="Carregando refeições..." />
      </Box>
    );
  }

  const daSemana = listaCS.filter((cs) => Number(cs.numeroSemana) === semana);
  // Dias úteis sempre aparecem; sábado e domingo só se tiverem refeições.
  const dias = [...new Set([...DIAS_UTEIS, ...daSemana.map((cs) => Number(cs.diaSemana))])].sort(
    (a, b) => ((a + 6) % 7) - ((b + 6) % 7),
  );
  const contagem = (n) => listaCS.filter((cs) => Number(cs.numeroSemana) === n).length;

  return (
    <Box>
      <CardapioSemanaForm
        cardapioSemana={editando}
        onClose={() => setEditando(null)}
        onSave={handleSave}
        onRemove={handleRemove}
        isSaving={isSaving}
      />

      <Tabs
        value={semana}
        onChange={(event, value) => setSemana(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {SEMANAS.map((n) => (
          <Tab
            key={n}
            value={n}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{n}ª Semana</span>
                <Chip size="small" label={contagem(n)} />
              </Stack>
            }
          />
        ))}
      </Tabs>

      <Grid container spacing={2}>
        {dias.map((dia) => {
          const cs = daSemana.find((item) => Number(item.diaSemana) === dia);
          return (
            <Grid key={dia} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card
                variant="outlined"
                sx={{ height: "100%", borderRadius: 2, ...(cs ? {} : { borderStyle: "dashed" }) }}
              >
                <CardActionArea
                  sx={{ height: "100%", alignItems: "stretch" }}
                  onClick={() =>
                    setEditando(cs ?? { cardapio, numeroSemana: semana, diaSemana: dia, eNovo: true })
                  }
                >
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} mb={1}>
                      {dayOfWeekName(dia)}
                    </Typography>
                    {cs ? (
                      <Stack spacing={1}>
                        {REFEICOES.map(({ campo, label }) => (
                          <Box key={campo}>
                            <Typography variant="caption" color="text.secondary">
                              {label}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                              {cs[campo] || "--"}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center" color="text.secondary" py={2}>
                        <AddIcon fontSize="small" />
                        <Typography variant="body2">Adicionar refeições</Typography>
                      </Stack>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default CardapioSemanaContainer;
